/**
 * Track 2: tiny audio facade (the only audio code in the main bundle).
 * The engine – synthesis, music, mixer graph – is a separate chunk that is downloaded only after the first user
 * gesture, which is also what the browser autoplay policy needs to start an AudioContext.
 * Calls made before the engine exists are dropped (sfx) or remembered (mood, settings).
 */
import { loadSettings, saveSettings, type AudioSettings, type Mood } from './settings.ts';
import type { AudioEngine, SfxName } from './engine.ts';

export type { SfxName } from './engine.ts';
export type { AudioSettings, Mood } from './settings.ts';

let engine: AudioEngine | null = null;
let loading: Promise<AudioEngine | null> | null = null;
let mood: Mood = 'calm';
let settings: AudioSettings = loadSettings();
let unlockInstalled = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
const GESTURES = ['pointerdown', 'keydown', 'touchend'] as const;

async function boot(): Promise<AudioEngine | null> {
  if (engine) return engine;
  loading ??= import('./engine.ts').then(async m => {
    const e = new m.AudioEngine();
    e.setSettings(settings);
    await e.resume();
    e.startMusic();
    e.setMood(mood);
    engine = e;
    emit();
    return e;
  }).catch(() => null);
  return loading;
}

/** Call once at start-up: the first pointer / key press unlocks audio (autoplay policy). */
export function installAudioUnlock(): void {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const onGesture = () => {
    void boot().then(async e => {
      await e?.resume();
      if (e?.state === 'running') for (const ev of GESTURES) window.removeEventListener(ev, onGesture, true);
    });
  };
  for (const ev of GESTURES) window.addEventListener(ev, onGesture, { capture: true, passive: true });
}

export const audio = {
  play(name: SfxName, opts?: { pan?: number; vol?: number }): void { engine?.play(name, opts); },
  setMood(m: Mood): void { mood = m; engine?.setMood(m); },
  getSettings(): AudioSettings { return settings; },
  setSettings(patch: Partial<AudioSettings>): void {
    settings = { ...settings, ...patch };
    saveSettings(settings);
    engine?.setSettings(patch);
    emit();
  },
  subscribe(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; },
  /** Engine started and the context is running (false until the first gesture). */
  get ready(): boolean { return engine?.state === 'running'; },
};
